import { useState, useMemo, useCallback } from 'react';
import { SearchInput } from '@/shared/ui/SearchInput';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { IconButton } from '@/shared/ui/IconButton';
import { paramTypeMeta, typography, semantic, radius } from '@/shared/theme/tokens';
import { clampToStep, fmtVal } from '../lib/leverMath';

const SIM_BRAND = semantic.blue;

const successColor = (isDark) => isDark ? '#34d399' : '#059669';
const warnColor    = (isDark) => isDark ? '#fbbf24' : '#d97706';

// ── Param row in layer accordion ─────────────────────────────────────────────────
function ParamRow({ param, isActive, onClick, c, isDark }) {
  const [hov, setHov] = useState(false);
  const meta = paramTypeMeta[param.type] ?? {};
  const outOfOpt = param.currentValue != null
    && ((param.optimumLow != null && param.currentValue < param.optimumLow)
    ||  (param.optimumHigh != null && param.currentValue > param.optimumHigh));

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        cursor: 'pointer', borderRadius: radius.md, userSelect: 'none',
        background: isActive
          ? (isDark ? 'rgba(37,99,235,0.14)' : 'rgba(37,99,235,0.07)')
          : hov ? c.bgHover : 'transparent',
        border: `1px solid ${isActive ? 'rgba(37,99,235,0.28)' : 'transparent'}`,
        transition: 'all 0.12s',
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: param.inModel ? SIM_BRAND : 'transparent',
        border: param.inModel ? 'none' : `1.5px solid ${c.border}`,
      }} />
      <span style={{
        flex: 1, fontSize: typography.fontSize.base, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: isActive ? c.textActive : c.textLabel,
        fontWeight: isActive ? 600 : 400,
      }}>{param.displayName}</span>
      <span style={{
        fontSize: typography.fontSize.xs, padding: '1px 6px', borderRadius: radius.sm, flexShrink: 0,
        background: c.bgMuted, color: c.textSubtle, border: `1px solid ${c.border}`, fontWeight: typography.fontWeight.semibold,
      }}>{meta.label}</span>
      <span style={{
        fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, flexShrink: 0, fontVariantNumeric: 'tabular-nums',
        color: outOfOpt ? warnColor(isDark) : c.textSubtle, minWidth: 48, textAlign: 'right',
      }}>
        {fmtVal(param.currentValue, param.step)}
        {param.unit && <span style={{ fontWeight: typography.fontWeight.normal, color: outOfOpt ? warnColor(isDark) : c.textSubtle, marginLeft: 2, fontSize: typography.fontSize.xs }}>{param.unit}</span>}
      </span>
      <span className="material-symbols-outlined" style={{
        fontSize: 14, flexShrink: 0, transition: 'color 0.1s',
        color: isActive ? SIM_BRAND : hov ? c.textMuted : 'transparent',
        fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0",
      }}>{isActive ? 'check_circle' : 'add_circle'}</span>
    </div>
  );
}

// ── Layer accordion ───────────────────────────────────────────────────────────────
function LayerAccordion({ layerName, params, adjustments, onToggle, c, isDark, search }) {
  const [open, setOpen] = useState(true);

  const visible = useMemo(() => {
    if (!search) return params;
    const q = search.toLowerCase();
    return params.filter((p) => p.displayName.toLowerCase().includes(q) || p.dataKey.toLowerCase().includes(q));
  }, [params, search]);

  if (visible.length === 0) return null;

  const activeCount = visible.filter((p) => p.dataKey in adjustments).length;

  return (
    <div style={{ marginBottom: 2 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', borderRadius: radius.md, cursor: 'pointer',
          background: open ? c.bgHover : 'none',
          border: 'none', textAlign: 'left', fontFamily: typography.fontFamily.body,
          transition: 'background 0.12s',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 13, color: c.textSubtle }}>layers</span>
        <span style={{ flex: 1, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: c.textLabel, letterSpacing: typography.letterSpacing.wide }}>
          {layerName}
        </span>
        {activeCount > 0 && (
          <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: SIM_BRAND, color: '#fff', borderRadius: radius.lg, padding: '1px 6px' }}>
            {activeCount}
          </span>
        )}
        <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, background: c.bgMuted, borderRadius: radius.md, padding: '1px 6px' }}>
          {visible.length}
        </span>
        <span className="material-symbols-outlined" style={{
          fontSize: 15, color: c.textSubtle,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
        }}>chevron_right</span>
      </button>
      {open && (
        <div className='flex flex-col gap-1' style={{ paddingLeft: 6, paddingBottom: 4, paddingTop: 4 }}>
          {visible.map((p) => (
            <ParamRow key={p.dataKey} param={p} isActive={p.dataKey in adjustments}
              onClick={() => onToggle(p)} c={c} isDark={isDark} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Adjustment slider ─────────────────────────────────────────────────────────────
function AdjustmentSlider({ param, value, onChange, onRemove, suggestedValue, c, isDark }) {
  // Local draft for the text input — lets user type freely; commits on blur/Enter
  const [draft, setDraft] = useState(null); // null = not editing

  const delta = param.currentValue != null ? value - param.currentValue : 0;
  const deltaStr = delta !== 0 ? `${delta > 0 ? '+' : ''}${fmtVal(delta, param.step)}` : '=';
  const deltaColor = delta > 0 ? successColor(isDark) : delta < 0 ? warnColor(isDark) : c.textSubtle;
  const range = param.sliderMax - param.sliderMin;
  const optLoPct = param.optimumLow != null && range > 0 ? ((param.optimumLow - param.sliderMin) / range) * 100 : null;
  const optHiPct = param.optimumHigh != null && range > 0 ? ((param.optimumHigh - param.sliderMin) / range) * 100 : null;
  const optMidPct = optLoPct != null && optHiPct != null ? (optLoPct + optHiPct) / 2 : null;

  const hasSuggestion = suggestedValue != null && suggestedValue !== value;
  const suggDelta = hasSuggestion && param.currentValue != null ? suggestedValue - param.currentValue : null;

  function commitDraft(raw) {
    setDraft(null);
    const next = clampToStep(raw, param.sliderMin, param.sliderMax, param.step);
    if (next === null) return; // non-numeric input → leave value unchanged
    onChange(param.dataKey, next);
  }

  return (
    <div style={{
      background: c.bgSubtle,
      border: `1px solid ${c.border}`, borderRadius: radius.lg, padding: '10px 12px', marginBottom: 6,
    }}>
      {/* Name row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ flex: 1, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {param.displayName}
        </span>
        <IconButton icon="close" size={14} onClick={() => onRemove(param.dataKey)} title="Remove" style={{ width: 28, height: 28, minWidth: 28 }} />
      </div>

      {/* Value row — editable input replaces static display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <input
          type="number"
          value={draft ?? fmtVal(value, param.step)}
          min={param.sliderMin}
          max={param.sliderMax}
          step={param.step}
          onFocus={(e) => { setDraft(e.target.value); e.target.select(); }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commitDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.target.blur(); }
            if (e.key === 'Escape') { setDraft(null); }
          }}
          style={{
            fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.extrabold, color: SIM_BRAND, fontVariantNumeric: 'tabular-nums',
            background: draft != null ? (isDark ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.07)') : 'transparent',
            border: draft != null ? `1.5px solid rgba(37,99,235,0.4)` : '1.5px solid transparent',
            borderRadius: radius.sm, padding: draft != null ? '1px 6px' : '1px 2px',
            width: `${Math.max(4, String(fmtVal(value, param.step)).length + 1)}ch`,
            outline: 'none', fontFamily: typography.fontFamily.body,
            MozAppearance: 'textfield',
            transition: 'border-color 0.12s, background 0.12s',
          }}
        />
        <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{param.unit}</span>
        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: deltaColor, marginLeft: 2 }}>({deltaStr})</span>
        {param.currentValue != null && (
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginLeft: 'auto' }}>
            now: {fmtVal(param.currentValue, param.step)}
          </span>
        )}
      </div>

      {/* Suggested setpoint inline hint */}
      {hasSuggestion && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
          padding: '5px 8px', borderRadius: radius.md,
          background: isDark ? 'rgba(37,99,235,0.1)' : 'rgba(37,99,235,0.06)',
          border: `1px solid ${isDark ? 'rgba(37,99,235,0.25)' : 'rgba(37,99,235,0.18)'}`,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13, color: SIM_BRAND }}>auto_fix_high</span>
          <span style={{ flex: 1, fontSize: typography.fontSize.sm, color: isDark ? '#93c5fd' : semantic.brandNavyMid }}>
            Suggested:{' '}
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtVal(suggestedValue, param.step)} {param.unit}
            </strong>
            {suggDelta != null && (
              <span style={{ marginLeft: 4, color: suggDelta > 0 ? successColor(isDark) : warnColor(isDark), fontWeight: typography.fontWeight.bold }}>
                ({suggDelta > 0 ? '+' : ''}{fmtVal(suggDelta, param.step)})
              </span>
            )}
          </span>
          <button
            onClick={() => onChange(param.dataKey, suggestedValue)}
            style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '2px 8px', borderRadius: radius.sm, cursor: 'pointer',
              background: SIM_BRAND, color: '#fff', border: 'none',
              fontFamily: typography.fontFamily.body,
            }}
          >
            Apply
          </button>
        </div>
      )}

      {/* Slider track */}
      <div style={{ position: 'relative', paddingBottom: 2 }}>
        {optLoPct != null && optHiPct != null && (
          <div style={{
            position: 'absolute', top: '50%', transform: 'translateY(-50%)',
            left: `${Math.max(0, optLoPct)}%`,
            width: `${Math.min(100, optHiPct) - Math.max(0, optLoPct)}%`,
            height: 6, background: isDark ? 'rgba(52,211,153,0.18)' : 'rgba(5,150,105,0.18)', borderRadius: radius.xs, pointerEvents: 'none',
          }} />
        )}
        {optMidPct != null && (
          <div style={{
            position: 'absolute', top: '50%', left: `${Math.max(0, Math.min(100, optMidPct))}%`,
            transform: 'translate(-50%, -50%)',
            width: 2, height: 12, background: successColor(isDark), borderRadius: radius.xs, pointerEvents: 'none',
          }} />
        )}
        <input
          type="range"
          min={param.sliderMin} max={param.sliderMax} step={param.step}
          value={value}
          onChange={(e) => onChange(param.dataKey, parseFloat(e.target.value))}
          style={{ width: '100%', accentColor: SIM_BRAND, cursor: 'pointer', margin: 0, display: 'block' }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
        <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{fmtVal(param.sliderMin, param.step)}</span>
        {(param.optimumLow != null || param.optimumHigh != null) && (
          <span style={{ fontSize: typography.fontSize.xs, color: successColor(isDark) }}>
            opt: {param.optimumLow != null && param.optimumHigh != null
              ? `${fmtVal(param.optimumLow, param.step)}–${fmtVal(param.optimumHigh, param.step)}`
              : param.optimumLow != null
                ? `≥ ${fmtVal(param.optimumLow, param.step)}`
                : `≤ ${fmtVal(param.optimumHigh, param.step)}`}
          </span>
        )}
        <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{fmtVal(param.sliderMax, param.step)}</span>
      </div>
    </div>
  );
}

// ── Main LeverPanel ───────────────────────────────────────────────────────────────
export function LeverPanel({
  config, adjustments, setAdjustments,
  suggestedSetpoints,
  onRunSimulation, onFetchSuggested, onApplySuggested, onReset,
  isPredicting, isFetchingSuggested,
  c, isDark,
}) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('parameters');
  const { parameters = [], optimalSetpoints = {} } = config ?? {};

  const allLayers = useMemo(() => {
    const layerSet = new Set();
    for (const p of parameters) {
      for (const l of (p.layers ?? ['Ungrouped'])) layerSet.add(l);
    }
    return [...layerSet].sort();
  }, [parameters]);

  const paramsByLayer = useMemo(() => {
    const map = {};
    for (const l of allLayers) map[l] = [];
    for (const p of parameters) {
      for (const l of (p.layers ?? ['Ungrouped'])) {
        if (map[l]) map[l].push(p);
      }
    }
    return map;
  }, [parameters, allLayers]);

  const paramMap = useMemo(() => Object.fromEntries(parameters.map((p) => [p.dataKey, p])), [parameters]);
  const activeKeys = Object.keys(adjustments);


  const handleToggle = useCallback((param) => {
    setAdjustments((prev) => {
      if (param.dataKey in prev) {
        const next = { ...prev };
        delete next[param.dataKey];
        return next;
      }
      return { ...prev, [param.dataKey]: param.currentValue ?? ((param.sliderMin + param.sliderMax) / 2) };
    });
  }, [setAdjustments]);

  const handleSliderChange = useCallback((key, val) => {
    setAdjustments((prev) => ({ ...prev, [key]: val }));
  }, [setAdjustments]);

  const handleRemove = useCallback((key) => {
    setAdjustments((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [setAdjustments]);

  const hasOptimal = Object.keys(optimalSetpoints).length > 0;
  const hasSuggestions = suggestedSetpoints != null && Object.keys(suggestedSetpoints).length > 0;

  const pendingSuggestionCount = hasSuggestions
    ? activeKeys.filter((k) => suggestedSetpoints[k] != null && suggestedSetpoints[k] !== adjustments[k]).length
    : 0;

  const TAB_STYLE = (active) => ({
    flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 600, cursor: 'pointer',
    border: 'none', borderRadius: 7,
    background: active ? (isDark ? 'rgba(37,99,235,0.18)' : 'rgba(37,99,235,0.1)') : 'transparent',
    color: active ? SIM_BRAND : c.textSubtle,
    fontFamily: typography.fontFamily.body,
    transition: 'all 0.15s',
  });

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius['2xl'], overflow: 'hidden',
    }}>
      <style>{`input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}`}</style>
      {/* Header */}
      <div style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, flex: 1 }}>Process Levers</span>
          {activeKeys.length > 0 && (
            <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: SIM_BRAND, color: '#fff', borderRadius: radius.lg, padding: '2px 8px' }}>
              {activeKeys.length} active
            </span>
          )}
        </div>
        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 2, padding: 3, borderRadius: radius.lg, background: c.bgMuted, border: `1px solid ${c.border}` }}>
          <button style={TAB_STYLE(tab === 'parameters')} onClick={() => setTab('parameters')}>
            Parameters
          </button>
          <button style={TAB_STYLE(tab === 'adjustments')} onClick={() => setTab('adjustments')}>
            Adjustments
            {activeKeys.length > 0 && (
              <span style={{
                display: 'inline-block', marginLeft: 5,
                fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: SIM_BRAND, color: '#fff',
                borderRadius: radius.md, padding: '0 5px', verticalAlign: 'middle',
              }}>
                {activeKeys.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Parameters tab — picker + collapsible active sliders stacked */}
      {tab === 'parameters' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          {/* Search */}
          <div style={{ padding: '8px 8px 4px', flexShrink: 0 }}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search parameters…"
              onClear={() => setSearch('')}
              size="sm"
              containerStyle={{ width: '100%' }}
            />
            <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: 5, paddingLeft: 2 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: SIM_BRAND, marginRight: 4 }} />
              filled = in prediction model · click row to add lever
            </div>
          </div>

          {/* Layer accordions */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px', minHeight: 0 }}>
            {allLayers.map((l) => (
              <LayerAccordion
                key={l} layerName={l} params={paramsByLayer[l] ?? []}
                adjustments={adjustments} onToggle={handleToggle}
                c={c} isDark={isDark} search={search}
              />
            ))}
          </div>

        </div>
      )}

      {/* Adjustments tab — full sliders view */}
      {tab === 'adjustments' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          {activeKeys.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 24, textAlign: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 36, color: c.textSubtle }}>tune</span>
              <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textLabel }}>No levers added yet</span>
              <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>Go to Parameters and click a row to add a lever.</span>
              <button
                onClick={() => setTab('parameters')}
                style={{
                  marginTop: 4, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, padding: '6px 16px', borderRadius: radius.md,
                  background: isDark ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.08)',
                  color: SIM_BRAND, border: `1px solid rgba(37,99,235,0.25)`,
                  cursor: 'pointer', fontFamily: typography.fontFamily.body,
                }}
              >
                Browse Parameters
              </button>
            </div>
          ) : (
            <>
              {/* Add more levers shortcut */}
              <div style={{ padding: '6px 10px 0', flexShrink: 0 }}>
                <button
                  onClick={() => setTab('parameters')}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    padding: '5px 10px', borderRadius: radius.md, cursor: 'pointer',
                    background: 'transparent',
                    border: `1px dashed ${c.border}`,
                    color: c.textSubtle, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
                    fontFamily: typography.fontFamily.body,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>add</span>
                  Add more levers
                </button>
              </div>
              {/* Apply-all suggestion banner */}
              {hasSuggestions && pendingSuggestionCount > 0 && (
                <div style={{
                  margin: '8px 10px 0', padding: '8px 10px',
                  borderRadius: radius.lg, display: 'flex', alignItems: 'center', gap: 8,
                  background: isDark ? 'rgba(37,99,235,0.1)' : 'rgba(37,99,235,0.06)',
                  border: `1px solid ${isDark ? 'rgba(37,99,235,0.3)' : 'rgba(37,99,235,0.2)'}`,
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15, color: SIM_BRAND, flexShrink: 0 }}>auto_fix_high</span>
                  <span style={{ flex: 1, fontSize: typography.fontSize.sm, color: isDark ? '#93c5fd' : semantic.brandNavyMid, lineHeight: 1.4 }}>
                    Suggestions ready for {pendingSuggestionCount} lever{pendingSuggestionCount > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => onApplySuggested(suggestedSetpoints)}
                    style={{
                      fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '3px 10px', borderRadius: radius.sm, cursor: 'pointer',
                      background: SIM_BRAND, color: '#fff', border: 'none', flexShrink: 0,
                      fontFamily: typography.fontFamily.body,
                    }}
                  >
                    Apply All
                  </button>
                </div>
              )}
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', minHeight: 0 }}>
                {activeKeys.map((key) => {
                  const p = paramMap[key];
                  if (!p) return null;
                  return (
                    <AdjustmentSlider
                      key={key} param={p} value={adjustments[key]}
                      onChange={handleSliderChange} onRemove={handleRemove}
                      suggestedValue={suggestedSetpoints?.[key] ?? null}
                      c={c} isDark={isDark}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Action bar */}
      <div style={{ padding: '10px 12px 12px', borderTop: `1px solid ${c.border}`, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Get Suggested — always available when levers active and optimal config exists */}
        {hasOptimal && activeKeys.length > 0 && (
          <button
            onClick={onFetchSuggested}
            disabled={isFetchingSuggested}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '7px 12px', borderRadius: radius.lg, cursor: isFetchingSuggested ? 'not-allowed' : 'pointer',
              background: 'transparent',
              border: `1.5px solid ${isDark ? 'rgba(37,99,235,0.35)' : 'rgba(37,99,235,0.3)'}`,
              color: SIM_BRAND, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
              fontFamily: typography.fontFamily.body,
              opacity: isFetchingSuggested ? 0.6 : 1,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
              {isFetchingSuggested ? 'hourglass_top' : 'auto_fix_high'}
            </span>
            {isFetchingSuggested ? 'Getting suggestions…' : hasSuggestions ? 'Refresh Suggestions' : 'Get Suggested Setpoints'}
          </button>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          {(activeKeys.length > 0) && (
            <button
              onClick={onReset}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                padding: '0 12px', borderRadius: radius.lg, cursor: 'pointer',
                background: 'transparent', border: `1px solid ${c.border}`,
                color: c.textSubtle, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                fontFamily: typography.fontFamily.body, flexShrink: 0,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>restart_alt</span>
              Reset
            </button>
          )}
          {tab === 'parameters' && activeKeys.length > 0 ? (
            <PrimaryButton
              variant="primary"
              size="md"
              icon="tune"
              onClick={() => setTab('adjustments')}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Adjust Levers
            </PrimaryButton>
          ) : (
            <PrimaryButton
              variant="primary"
              size="md"
              icon={isPredicting ? 'hourglass_top' : activeKeys.length === 0 ? 'playlist_add' : 'play_arrow'}
              onClick={activeKeys.length === 0 ? () => setTab('parameters') : onRunSimulation}
              disabled={isPredicting}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              {isPredicting ? 'Running…' : activeKeys.length === 0 ? 'Select Parameters first' : 'Run Simulation'}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}
